// RigSentinel Calc — shared report storage factory. Each report type creates
// its own isolated localStorage namespace via makeReportStorage(key).
// Malformed JSON is silently treated as empty — never crashes the app.
//
// Phase A: write paths (save/remove) require RigSentinel Pro. Read paths
// stay public so Preview users can still browse pre-existing drafts.
import { getCachedEntitlement } from "../entitlement";
import { isProActive, FeatureLockedError } from "../featureAccess";
import { scoped } from "../accountScope";

const safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

function _requireProForWrite(action) {
  if (!isProActive(getCachedEntitlement())) {
    throw new FeatureLockedError(
      `${action} is available with RigSentinel Pro.`,
      { feature: "report.storage.write" },
    );
  }
}

export function makeReportStorage(KEY) {
  return {
    KEY,
    getAll() {
      return safeParse(scoped.getItem(KEY), []);
    },
    get(id) {
      return this.getAll().find((d) => d.id === id) || null;
    },
    save(state) {
      _requireProForWrite("Saving a report draft");
      const list = this.getAll();
      const now = new Date().toISOString();
      let item;
      if (state.id) {
        item = { ...state, updatedAt: now };
        const idx = list.findIndex((d) => d.id === state.id);
        if (idx >= 0) list[idx] = item;
        else list.unshift(item);
      } else {
        item = {
          ...state,
          id: `${KEY.split(".")[1] || "rep"}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          createdAt: now,
          updatedAt: now,
        };
        list.unshift(item);
      }
      scoped.setItem(KEY, JSON.stringify(list.slice(0, 100)));
      return item;
    },
    remove(id) {
      _requireProForWrite("Deleting a report draft");
      const list = this.getAll().filter((d) => d.id !== id);
      scoped.setItem(KEY, JSON.stringify(list));
    },
  };
}

export const dmrStore  = makeReportStorage("rsc.dailyMudReport.drafts.v1");
export const tripStore = makeReportStorage("rsc.tripSheet.drafts.v1");
export const bitStore  = makeReportStorage("rsc.bitRunRecord.drafts.v1");
export const cmtStore  = makeReportStorage("rsc.cementingReport.drafts.v1");
export const bopStore  = makeReportStorage("rsc.bopTestSheet.drafts.v1");
export const hseStore  = makeReportStorage("rsc.hseIncidentForm.drafts.v1");
export const tbtStore  = makeReportStorage("rsc.toolboxTalk.drafts.v1");
export const ptbStore  = makeReportStorage("rsc.preTourBriefing.drafts.v1");
export const pjbStore  = makeReportStorage("rsc.preJobBriefing.drafts.v1");
export const jsaStore  = makeReportStorage("rsc.jsaGenerator.drafts.v1");

export const dtRStore  = makeReportStorage("rsc.downtimeReport.drafts.v1");
export const eqfStore  = makeReportStorage("rsc.equipmentFailureReport.drafts.v1");
export const hndStore  = makeReportStorage("rsc.handoverNotes.drafts.v1");
export const dosStore  = makeReportStorage("rsc.dailyOperationsSummary.drafts.v1");
export const lotStore  = makeReportStorage("rsc.lotFitReport.drafts.v1");
export const sbopStore = makeReportStorage("rsc.subseaBopTestReport.drafts.v1");
export const sfbopStore = makeReportStorage("rsc.surfaceBopTestReport.drafts.v1");
export const ksSfDevStore  = makeReportStorage("rsc.surfaceKickSheetDeviated.drafts.v1");
export const ksSfHorStore  = makeReportStorage("rsc.surfaceKickSheetHorizontal.drafts.v1");
export const ksSfVerStore  = makeReportStorage("rsc.surfaceKickSheetVertical.drafts.v1");
export const ksSsDevStore  = makeReportStorage("rsc.subseaKickSheetDeviated.drafts.v1");
export const ksSsHorStore  = makeReportStorage("rsc.subseaKickSheetHorizontal.drafts.v1");
export const ksSsVerStore  = makeReportStorage("rsc.subseaKickSheetVertical.drafts.v1");

